import { Controller } from "@hotwired/stimulus"

// Handles admin section interactivity on the project show page:
// - Phase transitions (activate dropdown)
// - Bidding payment modal (Stripe Elements for new CC, existing card, EFT)
// - Status changes (modal orchestration)
// - Collapsible sections (bidding history, PDF config)
// - Bidding phase dropdown (dev vs construction)
// - Witness signing
export default class extends Controller {
  static targets = [
    "activateSelect", "activateChangeBtn", "activateBadge",
    "statusSelect", "statusChangeBtn", "statusBadge", "statusHiddenField", "statusModalTitle", "onHoldReason", "onHoldValidation",
    "biddingOptions", "statusConfirmText",
    "biddingHistoryBody", "biddingHistoryChevron",
    "pdfConfigBody", "pdfConfigChevron",
    "exportDataBody", "exportDataChevron",
    "exportModal", "exportItem", "exportSubmitBtn", "exportOpenBtn", "exportError",
    "mergeDuplicateBody", "mergeDuplicateChevron",
    "biddingPhaseSelect", "biddingPhaseSubmitBtn", "biddingPhaseModalBtn", "biddingPhaseBadge",
    "witnessCount",
    "confirmModal", "confirmModalMessage",
    "paymentModal", "paymentFrame", "paymentMethodSelect", "newCardSection", "eftSection",
    "ccChargeId", "existingCardId", "saveCard",
    "paymentSubmitBtn", "paymentError",
    "finishNextBtn", "finishOverride", "evalTabs", "skipConfirmModal"
  ]

  static values = {
    projectId: Number,
    projectName: String,
    projectNumber: String,
    csrfToken: String,
    requiresPayment: Boolean
  }

  // ── Toggle badge/button on dropdown change ────────────────────────────

  activateSelectChanged() {
    const hasValue = !!this.activateSelectTarget.value
    if (this.hasActivateBadgeTarget) {
      this.activateBadgeTarget.style.display = hasValue ? "none" : ""
    }
    if (this.hasActivateChangeBtnTarget) {
      this.activateChangeBtnTarget.style.display = hasValue ? "" : "none"
    }
  }

  statusSelectChanged() {
    const hasValue = !!this.statusSelectTarget.value
    if (this.hasStatusBadgeTarget) {
      this.statusBadgeTarget.style.display = hasValue ? "none" : ""
    }
    if (this.hasStatusChangeBtnTarget) {
      this.statusChangeBtnTarget.style.display = hasValue ? "" : "none"
    }
  }

  // ── Phase transition (dev/RE activate dropdown) ───────────────────────

  activatePhaseChange(event) {
    const action = this.activateSelectTarget.value
    if (!action) return

    // Show payment modal for bidding activation
    if (action === "activate_for_bidding" && this.hasPaymentModalTarget) {
      this._pendingAction = action
      this._showPaymentError("")
      this.paymentModalTarget.classList.remove("hidden")
      this._loadPaymentFrame()
      return
    }

    const messages = {
      archive: "Are you sure you want to archive this project?",
      unarchive: "Are you sure you want to unarchive this project?",
      activate_for_development: "Are you sure you want to move this project to development?",
      activate_for_construction: "Are you sure you want to move this project to construction?"
    }

    this._pendingAction = action
    this.confirmModalMessageTarget.textContent = messages[action] || "Are you sure you want to proceed?"
    this.confirmModalTarget.classList.remove("hidden")
  }

  confirmPhaseChange() {
    this.confirmModalTarget.classList.add("hidden")
    const action = this._pendingAction
    if (!action) return

    this._executePhaseChange(action)
  }

  cancelPhaseChange() {
    this.confirmModalTarget.classList.add("hidden")
    this.activateSelectTarget.value = ""
    this.activateSelectChanged()
    this._pendingAction = null
  }

  _executePhaseChange(action, params = "") {
    const urls = {
      archive: `/projects/${this.projectIdValue}/archive`,
      unarchive: `/projects/${this.projectIdValue}/unarchive`,
      activate_for_development: `/projects/${this.projectIdValue}/activate_for_development`,
      activate_for_construction: `/projects/${this.projectIdValue}/activate_for_construction`,
      activate_for_bidding: `/projects/${this.projectIdValue}/activate_for_bidding`
    }

    let url = urls[action]
    if (!url) return
    if (params) url += `?${params}`

    fetch(url, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfTokenValue,
        "Accept": "text/html"
      },
      redirect: "manual"
    }).then(response => {
      if (response.type === "opaqueredirect") {
        // Flash is stored in session but not yet consumed (redirect wasn't followed).
        // Navigate to the project page so the flash toast is displayed.
        window.location.href = `/projects/${this.projectIdValue}`
      } else if (response.ok) {
        window.location.reload()
      } else {
        response.text().then(text => {
          try {
            const data = JSON.parse(text)
            if (data.error) alert(data.errorMsg || "An error occurred")
          } catch {
            window.location.reload()
          }
        })
      }
    })
  }

  stopPropagation(event) {
    event.stopPropagation()
  }

  // ── Bidding payment modal ─────────────────────────────────────────────

  paymentMethodChanged(event) {
    const val = event.currentTarget.value
    if (this.hasEftSectionTarget) {
      this.eftSectionTarget.classList.toggle("hidden", val !== "eft")
    }
    if (this.hasNewCardSectionTarget) {
      this.newCardSectionTarget.classList.toggle("hidden", val !== "new_cc")
      if (val === "new_cc") this._initStripeElements()
    }
  }

  async confirmBiddingPayment() {
    this._showPaymentError("")
    const btn = this.paymentSubmitBtnTarget
    const originalText = btn.textContent
    btn.disabled = true
    btn.textContent = "Processing..."

    try {
      if (!this.requiresPaymentValue) {
        // Unlimited or pre-paid — no payment needed
        this.paymentModalTarget.classList.add("hidden")
        this._executePhaseChange("activate_for_bidding")
        return
      }

      const method = this.hasPaymentMethodSelectTarget ? this.paymentMethodSelectTarget.value : "eft"

      if (method === "new_cc") {
        const ok = await this._processNewCardPayment()
        if (!ok) { this._resetPaymentBtn(btn, originalText); return }

        const chargeId = this.ccChargeIdTarget.value
        this.paymentModalTarget.classList.add("hidden")
        this._executePhaseChange("activate_for_bidding", `payment_method=credit_card&charge_id=${encodeURIComponent(chargeId)}`)

      } else if (method === "existing_card") {
        const ok = await this._processExistingCardPayment()
        if (!ok) { this._resetPaymentBtn(btn, originalText); return }

        const chargeId = this.ccChargeIdTarget.value
        this.paymentModalTarget.classList.add("hidden")
        this._executePhaseChange("activate_for_bidding", `payment_method=existing_card&charge_id=${encodeURIComponent(chargeId)}`)

      } else {
        // EFT — no charge, just pass the payment method
        this.paymentModalTarget.classList.add("hidden")
        this._executePhaseChange("activate_for_bidding", "payment_method=eft")
      }
    } catch (e) {
      this._showPaymentError(e.message || "Payment failed. Please try again.")
      this._resetPaymentBtn(btn, originalText)
    }
  }

  cancelPayment() {
    this.paymentModalTarget.classList.add("hidden")
    if (this.hasActivateSelectTarget) {
      this.activateSelectTarget.value = ""
      this.activateSelectChanged()
    }
    this._pendingAction = null
  }

  _loadPaymentFrame() {
    if (!this.hasPaymentFrameTarget) return
    const frame = this.paymentFrameTarget
    // Only load once — trigger the turbo frame src on first open
    if (!frame.src && frame.dataset.src) {
      frame.src = frame.dataset.src
      // After turbo frame loads, init Stripe if the new_cc section is visible
      frame.addEventListener("turbo:frame-load", () => {
        this._initStripeIfNeeded()
      }, { once: true })
    } else {
      this._initStripeIfNeeded()
    }
  }

  _initStripeIfNeeded() {
    if (!this.hasPaymentMethodSelectTarget) return
    const val = this.paymentMethodSelectTarget.value
    if (val === "new_cc") {
      this._initStripeElements()
    }
  }

  _initStripeElements() {
    if (this._stripeCardElement) return
    const keyEl = this.element.querySelector("[data-stripe-key]")
    if (!keyEl) return
    const key = keyEl.dataset.stripeKey
    if (!key || typeof Stripe === "undefined") return

    this._stripe = Stripe(key)
    const elements = this._stripe.elements()
    const cs = getComputedStyle(document.documentElement)
    this._stripeCardElement = elements.create("card", {
      style: {
        base: { fontSize: "14px", color: cs.getPropertyValue("--color-text-heading").trim() || "#1f2937", "::placeholder": { color: cs.getPropertyValue("--color-text-muted").trim() || "#9ca3af" } },
        invalid: { color: cs.getPropertyValue("--color-danger").trim() || "#dc2626" }
      }
    })
    this._stripeCardElement.mount("#admin-card-element")
    this._stripeCardElement.on("change", (event) => {
      const errEl = document.getElementById("admin-card-errors")
      if (errEl) errEl.textContent = event.error ? event.error.message : ""
    })
  }

  async _processNewCardPayment() {
    if (!this._stripe || !this._stripeCardElement) {
      this._showPaymentError("Please enter your credit card details.")
      return false
    }

    const { token, error } = await this._stripe.createToken(this._stripeCardElement)
    if (error) {
      this._showPaymentError(error.message)
      return false
    }

    const body = new FormData()
    body.append("stripe_token", token.id)
    body.append("project_number", this.projectNumberValue)
    body.append("project_name", this.projectNameValue)
    if (this.hasSaveCardTarget && this.saveCardTarget.checked) {
      body.append("save_card", "1")
    }

    const resp = await fetch("/projects/bidding_cc_payment", {
      method: "POST",
      headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfTokenValue },
      body
    })
    const data = await resp.json()
    if (data.error) {
      this._showPaymentError(data.message || "Payment failed.")
      return false
    }
    this.ccChargeIdTarget.value = data.charge_id
    return true
  }

  async _processExistingCardPayment() {
    const cardId = this.hasExistingCardIdTarget ? this.existingCardIdTarget.value : ""
    if (!cardId) {
      this._showPaymentError("No saved card found.")
      return false
    }

    const body = new FormData()
    body.append("card_id", cardId)
    body.append("project_number", this.projectNumberValue)
    body.append("project_name", this.projectNameValue)

    const resp = await fetch("/projects/bidding_cc_existing_card_payment", {
      method: "POST",
      headers: { "Accept": "application/json", "X-CSRF-Token": this.csrfTokenValue },
      body
    })
    const data = await resp.json()
    if (data.error) {
      this._showPaymentError(data.message || "Payment failed.")
      return false
    }
    this.ccChargeIdTarget.value = data.charge_id
    return true
  }

  _showPaymentError(msg) {
    if (!this.hasPaymentErrorTarget) return
    if (msg) {
      this.paymentErrorTarget.textContent = msg
      this.paymentErrorTarget.classList.remove("hidden")
    } else {
      this.paymentErrorTarget.textContent = ""
      this.paymentErrorTarget.classList.add("hidden")
    }
  }

  _resetPaymentBtn(btn, text) {
    btn.disabled = false
    btn.textContent = text
  }

  // ── Status change (construction phase) ────────────────────────────────

  openStatusModal(event) {
    const status = this.statusSelectTarget.value
    if (!status) return

    if (status === "completed") {
      this._openModal("finish-project")
      return
    }

    // Set hidden field in status change modal
    if (this.hasStatusHiddenFieldTarget) {
      this.statusHiddenFieldTarget.value = status
    }

    const isOnHold = status === "on_hold"
    const isBidOpen = status === "bid_open"

    // Show/hide on_hold reason
    if (this.hasOnHoldReasonTarget) {
      this.onHoldReasonTarget.closest(".on-hold-reason-wrapper").classList.toggle("hidden", !isOnHold)
      if (isOnHold) {
        this.onHoldReasonTarget.value = ""
        this._bindOnHoldValidation()
      } else {
        this._unbindOnHoldValidation()
      }
    }

    // Hide validation message when switching statuses
    if (this.hasOnHoldValidationTarget) {
      this.onHoldValidationTarget.classList.add("hidden")
    }

    // Show/hide bidding options
    if (this.hasBiddingOptionsTarget) {
      this.biddingOptionsTarget.classList.toggle("hidden", !isBidOpen)
    }

    // Show confirmation text for simple statuses, hide for on_hold/bid_open
    if (this.hasStatusConfirmTextTarget) {
      const showConfirm = !isOnHold && !isBidOpen
      this.statusConfirmTextTarget.classList.toggle("hidden", !showConfirm)
      if (showConfirm) {
        const labels = {
          active: "Active",
          turn_over_phase: "Turn Over Phase",
          on_hold: "On Hold",
          dev: "Design"
        }
        const label = labels[status] || status
        this.statusConfirmTextTarget.querySelector("p").textContent =
          `Are you sure you want to change this project's status to "${label}"?`
      }
    }

    // Update modal title for bidding
    if (this.hasStatusModalTitleTarget) {
      this.statusModalTitleTarget.textContent = isBidOpen
        ? "Send Back to Bidding"
        : "Change Project Status"
    }

    this._openModal("status-change-project")
  }

  validateOnHoldSubmit(event) {
    if (!this.hasStatusHiddenFieldTarget || this.statusHiddenFieldTarget.value !== "on_hold") return
    if (!this.hasOnHoldReasonTarget) return

    const words = this.onHoldReasonTarget.value.trim().split(/\s+/).filter(w => w.length > 0)
    if (words.length < 5) {
      event.preventDefault()
      if (this.hasOnHoldValidationTarget) {
        this.onHoldValidationTarget.classList.remove("hidden")
      }
    }
  }

  _bindOnHoldValidation() {
    this._unbindOnHoldValidation()
    this._onHoldInputHandler = () => {
      if (!this.hasOnHoldReasonTarget || !this.hasOnHoldValidationTarget) return
      const words = this.onHoldReasonTarget.value.trim().split(/\s+/).filter(w => w.length > 0)
      if (words.length >= 5) {
        this.onHoldValidationTarget.classList.add("hidden")
      }
    }
    this.onHoldReasonTarget.addEventListener("input", this._onHoldInputHandler)
  }

  _unbindOnHoldValidation() {
    if (this._onHoldInputHandler && this.hasOnHoldReasonTarget) {
      this.onHoldReasonTarget.removeEventListener("input", this._onHoldInputHandler)
      this._onHoldInputHandler = null
    }
  }

  // ── Evaluation flow (finish project with providers) ──────────────────

  openEvaluationModal() {
    this._openModal("finish-project", "close")
    this._openModal("evaluation-project")
  }

  switchEvalTab(event) {
    const idx = parseInt(event.currentTarget.dataset.tabIndex)
    // Toggle panes
    this.element.querySelectorAll(".eval-tab-pane").forEach((pane, i) => {
      pane.classList.toggle("hidden", i !== idx)
    })
    // Toggle tab active styles
    if (this.hasEvalTabsTarget) {
      this.evalTabsTarget.querySelectorAll("button").forEach((btn, i) => {
        btn.classList.toggle("border-brand", i === idx)
        btn.classList.toggle("text-brand", i === idx)
        btn.classList.toggle("border-transparent", i !== idx)
        btn.classList.toggle("text-text-muted", i !== idx)
      })
    }
  }

  updateFinalRating(event) {
    const { builderId } = event.detail
    if (!builderId) return

    // Find all star-rating hidden fields for this builder
    const pane = this.element.querySelector(`.eval-tab-pane:has([data-star-rating-builder-id-value="${builderId}"])`)
    if (!pane) return

    const fields = pane.querySelectorAll('[data-controller="star-rating"] [data-star-rating-target="hiddenField"]')
    let sum = 0, count = 0
    fields.forEach(f => {
      const v = parseFloat(f.value)
      if (v > 0) { sum += v; count++ }
    })
    const avg = count > 0 ? sum / count : 0

    // Update the final rating stars display
    const container = this.element.querySelector(`.eval-final-rating[data-builder-id="${builderId}"]`)
    if (!container) return

    const fullStars = Math.round(avg)
    let html = ""
    for (let i = 1; i <= 5; i++) {
      const fill = i <= fullStars ? "#f5a623" : "#d1d5db"
      html += `<svg class="w-4 h-4" viewBox="0 0 20 20" fill="${fill}"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>`
    }
    container.innerHTML = `<span class="inline-flex items-center gap-px">${html}</span>`
  }

  toggleFinishOverride() {
    const checked = this.hasFinishOverrideTarget && this.finishOverrideTarget.checked
    if (this.hasFinishNextBtnTarget) {
      this.finishNextBtnTarget.disabled = !checked
    }
  }

  showSkipConfirm() {
    if (this.hasSkipConfirmModalTarget) {
      this.skipConfirmModalTarget.classList.remove("hidden")
    }
  }

  hideSkipConfirm() {
    if (this.hasSkipConfirmModalTarget) {
      this.skipConfirmModalTarget.classList.add("hidden")
    }
  }

  confirmSkipEvaluations() {
    this.hideSkipConfirm()
    fetch(`/projects/${this.projectIdValue}/finalize_without_evaluations`, {
      method: "POST",
      headers: {
        "X-CSRF-Token": this.csrfTokenValue,
        "Accept": "text/html"
      },
      redirect: "manual"
    }).then(() => { window.location.reload() })
  }

  // ── Collapsible sections ──────────────────────────────────────────────

  toggleBiddingHistory() {
    this._toggle(this.biddingHistoryBodyTarget, this.biddingHistoryChevronTarget)
  }

  togglePdfConfig() {
    this._toggle(this.pdfConfigBodyTarget, this.pdfConfigChevronTarget)
  }

  toggleExportData() {
    this._toggle(this.exportDataBodyTarget, this.exportDataChevronTarget)
  }

  toggleMergeDuplicate() {
    if (!this.hasMergeDuplicateBodyTarget) return
    this._toggle(this.mergeDuplicateBodyTarget, this.mergeDuplicateChevronTarget)
  }

  // ── Export Project Data pop-up (Phelipe 2026-09-14) ─────────────────
  // Everything starts ticked; the request carries only the ticked items.

  openExportModal() {
    if (!this.hasExportModalTarget) return
    this.exportItemTargets.forEach(cb => { cb.checked = true })
    this.exportItemChanged()
    this.exportModalTarget.classList.remove("hidden")
  }

  closeExportModal() {
    if (this.hasExportModalTarget) this.exportModalTarget.classList.add("hidden")
  }

  exportSelectAll() {
    this.exportItemTargets.forEach(cb => { cb.checked = true })
    this.exportItemChanged()
  }

  exportSelectNone() {
    this.exportItemTargets.forEach(cb => { cb.checked = false })
    this.exportItemChanged()
  }

  exportItemChanged() {
    const any = this.exportItemTargets.some(cb => cb.checked)
    if (this.hasExportErrorTarget) this.exportErrorTarget.classList.toggle("hidden", any)
  }

  submitExport() {
    const picked = this.exportItemTargets.filter(cb => cb.checked).map(cb => cb.value)
    if (picked.length === 0) {
      if (this.hasExportErrorTarget) this.exportErrorTarget.classList.remove("hidden")
      return
    }
    const btn = this.exportSubmitBtnTarget
    const base = btn.dataset.url
    const query = picked.map(v => `sections[]=${encodeURIComponent(v)}`).join("&")
    btn.disabled = true
    btn.classList.add("opacity-50", "cursor-not-allowed")

    fetch(`${base}${base.includes("?") ? "&" : "?"}${query}`, {
      method: "GET",
      headers: { "X-CSRF-Token": this.csrfTokenValue, "Accept": "text/html" }
    }).then(response => {
      btn.disabled = false
      btn.classList.remove("opacity-50", "cursor-not-allowed")
      if (response.ok) {
        this.closeExportModal()
        if (this.hasExportOpenBtnTarget) {
          this.exportOpenBtnTarget.textContent = "Requested"
          this.exportOpenBtnTarget.disabled = true
          this.exportOpenBtnTarget.classList.add("opacity-50", "cursor-not-allowed")
        }
        const all = picked.length === this.exportItemTargets.length
        const requested = btn.dataset.i18nRequested || "Export requested. You will receive an email with the download link."
        const some = btn.dataset.i18nRequestedSome || "Export of %{count} items requested. You will receive an email with the download link."
        this._showToast(all ? requested : some.replace("%{count}", picked.length))
      } else {
        this._showToast(btn.dataset.i18nFailed || "Something went wrong. Please try again.", "error")
      }
    }).catch(() => {
      btn.disabled = false
      btn.classList.remove("opacity-50", "cursor-not-allowed")
      this._showToast(btn.dataset.i18nFailed || "Something went wrong. Please try again.", "error")
    })
  }

  requestExport(event) {
    const url = event.currentTarget.dataset.url
    const button = event.currentTarget
    button.disabled = true
    button.classList.add("opacity-50", "cursor-not-allowed")

    fetch(url, {
      method: "GET",
      headers: {
        "X-CSRF-Token": this.csrfTokenValue,
        "Accept": "text/html"
      }
    }).then(response => {
      if (response.ok) {
        button.textContent = "Requested"
        this._showToast("Export requested. You will receive an email with the download link.")
      } else {
        button.disabled = false
        button.classList.remove("opacity-50", "cursor-not-allowed")
        this._showToast("Something went wrong. Please try again.", "error")
      }
    }).catch(() => {
      button.disabled = false
      button.classList.remove("opacity-50", "cursor-not-allowed")
      this._showToast("Something went wrong. Please try again.", "error")
    })
  }

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.setAttribute("data-controller", "flash")
    el.innerHTML = `${icon}<span>${message}</span>`
    document.body.appendChild(el)
  }

  _openModal(id, action = "open") {
    const modal = document.querySelector(`[data-modal-id-value="${id}"]`)
    if (modal) {
      const ctrl = this.application.getControllerForElementAndIdentifier(modal, "modal")
      if (ctrl) ctrl[action]()
    }
  }

  _toggle(body, chevron) {
    if (!body) return
    const isCollapsed = body.style.maxHeight === "0px" || !body.style.maxHeight
    if (isCollapsed) {
      body.style.maxHeight = body.scrollHeight + "px"
      body.style.opacity = "1"
      if (chevron) chevron.style.transform = "rotate(90deg)"
    } else {
      body.style.maxHeight = "0px"
      body.style.opacity = "0"
      if (chevron) chevron.style.transform = ""
    }
  }

  // ── Bidding phase dropdown ────────────────────────────────────────────

  biddingPhaseChange(event) {
    const value = this.biddingPhaseSelectTarget.value
    if (this.hasBiddingPhaseBadgeTarget) {
      this.biddingPhaseBadgeTarget.style.display = value ? "none" : ""
    }
    if (this.hasBiddingPhaseSubmitBtnTarget) {
      this.biddingPhaseSubmitBtnTarget.classList.toggle("hidden", value !== "dev")
    }
    if (this.hasBiddingPhaseModalBtnTarget) {
      this.biddingPhaseModalBtnTarget.classList.toggle("hidden", value !== "construction")
    }
  }

  // ── Witness signing ───────────────────────────────────────────────────

  signWitness(event) {
    event.preventDefault()
    const witnessId = event.currentTarget.dataset.witnessId

    fetch(`/projects/${this.projectIdValue}/sign_witness?witness=${witnessId}`, {
      method: "GET",
      headers: {
        "X-CSRF-Token": this.csrfTokenValue,
        "Accept": "application/json"
      }
    })
      .then(r => r.json())
      .then(data => {
        if (data.message === "signed" && this.hasWitnessCountTarget) {
          const current = parseInt(this.witnessCountTarget.textContent) || 0
          this.witnessCountTarget.textContent = Math.min(current + 1, 2)
        }
        window.location.reload()
      })
  }
}
